import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Button } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import moment from "moment";
import { useHistory } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { numberAsCurrency, minPurchasableTickets, initialTicketSelection, campaignBannerAspectRatio } from '../utils/index';
import SubcampaignHint from './SubcampaignHint';
import SelectionBox from '../pages/Campaign/SelectionBox';
import axios from 'axios';
import Swal from 'sweetalert2';
import Cookie from 'js-cookie';
import RoundUpPurchaseModal from './RoundUpPurchaseModal';
import PictureBanner from './PictureBanner';
import { trackEvent } from '../utils';

const useStyles = makeStyles((theme) => ({
  // === LIST MODE ===
  root: {
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: '12px',
    display: 'flex',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.13)',
    },
  },
  imageContainer: {
    flexShrink: 0,
    width: '110px',
    [theme.breakpoints.down('xs')]: {
      width: '90px',
    },
    '& img, & video': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
    },
  },
  details: {
    padding: '10px 12px',
    textAlign: 'left',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minWidth: 0,
  },
  rightCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: '10px 12px 10px 6px',
    flexShrink: 0,
    gap: '6px',
  },
  topMetaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '4px',
  },
  priceDateRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginTop: '4px',
  },
  code: {
    fontSize: '0.72rem',
    color: '#666666',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  dateBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#D4EDDA',
    color: '#000000',
    borderRadius: '20px',
    padding: '2px 8px',
    fontSize: '0.72rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    '& img': {
      width: '12px',
      height: '12px',
      filter: 'brightness(0) saturate(100%)',
    },
  },
  dateBadgeCompleted: {
    backgroundColor: '#F8D7DA',
    color: '#721C24',
  },
  dateBadgeConcluded: {
    backgroundColor: '#DDDDDD',
    color: '#4A4A4A',
  },

  // === FIRST/HERO MODE ===
  rootFirst: {
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: '16px',
  },
  // Sem altura fixa: a caixa segue a proporção do banner (aspect-ratio vem
  // inline, pelo banner em uso), para a arte nunca ser recortada.
  imageContainerFirst: {
    position: 'relative',
    '& img, & video': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
    },
  },
  badgeOverlay: {
    position: 'absolute',
    top: '10px',
    left: '10px',
  },
  detailsFirst: {
    padding: '14px 16px 16px',
    textAlign: 'left',
  },
  titleFirstRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '6px',
  },
  priceCodeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    gap: '10px',
    flexWrap: 'nowrap',
  },
  subtitleFirst: {
    color: '#666666',
    fontSize: '0.95rem',
    marginBottom: '10px',
    lineHeight: 1.3,
    textAlign: 'left',
  },

  // === SHARED ===
  title: {
    fontWeight: 700,
    color: '#1A1A1A',
    fontSize: '1rem',
    lineHeight: 1.3,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9rem',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      display: '-webkit-box',
      overflow: 'hidden',
    },
  },
  titleFirst: {
    fontWeight: 700,
    color: '#1A1A1A',
    fontSize: '1.05rem',
    lineHeight: 1.3,
    flex: 1,
  },
  subtitle: {
    color: '#666666',
    fontSize: '0.8rem',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
    display: '-webkit-box',
    overflow: 'hidden',
    marginBottom: '4px',
  },
  price: {
    color: '#3B7E3D',
    fontWeight: 400,
    fontSize: '0.85rem',
  },
  priceFirst: {
    color: '#3B7E3D',
    fontWeight: 400,
    fontSize: '1rem',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  priceValueBold: {
    fontWeight: 700,
  },
  status: {
    display: 'inline-block',
    borderRadius: '20px',
    padding: '2px 10px',
    fontWeight: 600,
    fontSize: '0.72rem',
    marginBottom: '5px',
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
    width: 'fit-content',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  comprarBtn: {
    width: '100%',
    backgroundColor: '#28A745',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '1rem',
    borderRadius: '8px',
    padding: '12px 14px',
    textTransform: 'none',
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: '#1E7E34',
    },
  },
  comprarBtnLabel: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'nowrap',
    gap: '0.65em',
    width: '100%',
  },
  comprarBtnIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.5rem',
    height: '1.5rem',
    minWidth: '1.5rem',
    minHeight: '1.5rem',
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    flexShrink: 0,
    boxSizing: 'border-box',
    marginRight: 0,
    alignSelf: 'center',
  },
  comprarBtnArrow: {
    fontSize: '0.8125em',
    color: '#28A745',
    fill: '#28A745',
    display: 'block',
  },
  comprarBtnTitle: {
    fontWeight: 700,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    minHeight: '1.5rem',
    fontSize: '1em',
    alignSelf: 'center',
  },
  comprarSubtitle: {
    fontSize: '0.78em',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.92)',
    lineHeight: 1,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '1.5rem',
    alignSelf: 'center',
  },
  codeFirst: {
    fontSize: '0.78rem',
    color: '#666666',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  dateBadgeFirst: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#D4EDDA',
    color: '#000000',
    borderRadius: '20px',
    padding: '3px 10px',
    fontSize: '0.78rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    '& img': {
      width: '13px',
      height: '13px',
      filter: 'brightness(0) saturate(100%)',
    },
  },
  dateBadgeFirstCompleted: {
    backgroundColor: '#F8D7DA',
    color: '#721C24',
  },
  dateBadgeFirstConcluded: {
    backgroundColor: '#DDDDDD',
    color: '#4A4A4A',
  },
  codeDateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'flex-end',
    width: 'auto',
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
  codeRowLeft: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    textAlign: 'right',
    flex: '0 0 auto',
  },
  dateRowRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    textAlign: 'right',
  },
  verMaisBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '11px 14px',
    borderRadius: '8px',
    border: '2px solid #2D6E2F',
    backgroundColor: 'transparent',
    color: '#2D6E2F',
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    marginTop: '8px',
    '&:hover': {
      backgroundColor: 'rgba(2, 51, 153, 0.05)',
    },
  },
  '@keyframes blink': {
    from: { opacity: 1.0 },
    '50%': { opacity: 0.5 },
    to: { opacity: 1.0 },
  },
  subcampaignHint: {
    marginTop: '8px',
    marginBottom: '10px',
    padding: '6px 10px',
    borderRadius: '8px',
    backgroundColor: '#FFF3CD',
    color: '#664D03',
    fontSize: '0.78rem',
    fontWeight: 600,
    lineHeight: 1.35,
  },
  subcampaignHintList: {
    marginTop: '6px',
    padding: '4px 8px',
    borderRadius: '6px',
    backgroundColor: '#FFF3CD',
    color: '#664D03',
    fontSize: '0.72rem',
    fontWeight: 600,
    lineHeight: 1.3,
  },
}));

export default function Campaign({campaign, currentUser, influencer, mode = 'list', link = true, showHeroButton = true, showHeroPrice = true, showHeroQuickPurchase = false, onBoostExpire = null}) {
  const classes = useStyles();
  const history = useHistory();
  moment.locale('pt-BR');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [purchaseLoading, setPurchaseLoading] = React.useState(false);
  const [selectedTickets, setSelectedTickets] = React.useState(() => initialTicketSelection(campaign));
  const [roundUpModal, setRoundUpModal] = React.useState({
    open: false,
    currentTickets: 0,
    suggestedTickets: 0,
    suggestedTotalFormatted: '',
  });

  function handleClick() {
    if (link)
      history.push(`/${influencer?.username || campaign.influencer_username}/site/campanhas/${campaign.id}`);
  }

  function isCompletedCampaignStatus(status) {
    return ['finished', 'waiting_drawn', 'sold_out'].includes(status);
  }

  function badgeStyleForStatus(status) {
    switch (status) {
      case 'finished':
      case 'waiting_drawn':
      case 'sold_out':
        return { backgroundColor: '#D5EBD5', color: '#2A5B2C' };
      case 'waiting_start':
        return { backgroundColor: '#D5EBD5', color: '#2A5B2C' };
      case 'near_time_limit':
      case 'near_tickets_limit':
        return { backgroundColor: '#F8D7DA', color: '#721C24', animation: 'blink 1s infinite' };
      default:
        return { backgroundColor: '#D4EDDA', color: '#155724' };
    }
  }

  function statusLabel() {
    return link ? campaign.t_user_status : campaign?.t_user_status?.replace('Clique e ', '');
  }

  function minTickets() {
    return minPurchasableTickets(campaign);
  }

  function maxTickets() {
    return campaign?.max_amount === 0 ? 1000000 : campaign?.max_amount;
  }

  function safeSetSelectedTickets(value) {
    if (value >= minTickets() && value <= maxTickets()) {
      setSelectedTickets(value);
    }
  }

  function submitQuickPurchaseOrder(ticketsAmount) {
    const username = influencer?.username || campaign.influencer_username;
    setPurchaseLoading(true);
    Swal.fire({
      title: 'Efetuando a compra',
      didOpen: () => {
        Swal.showLoading();
      },
      allowEscapeKey: false,
      allowOutsideClick: false,
      showConfirmButton: false,
    });

    axios
      .post('/api/v1/purchase_orders', {
        purchase_order: {
          campaign_id: campaign.id,
          tickets_amount: ticketsAmount,
        },
      })
      .then((response) => {
        trackEvent(
          'InitiateCheckout',
          {
            value: parseFloat(response.data?.total_value) || 0,
            currency: 'BRL'
          },
          { dedupe: false }
        );
        Swal.close();
        history.push(`/${username}/site/compras/${response.data.id}`);
      })
      .catch((error) => {
        let errorMessages;
        try {
          errorMessages = Object.values(error.response.data).join('<br/>');
        } catch (e) {
          errorMessages = 'Erro desconhecido';
        }

        Swal.fire({
          title: 'Erro ao efetuar a compra',
          html: errorMessages,
          icon: 'error',
        });
      })
      .finally(() => {
        setPurchaseLoading(false);
      });
  }

  function quickPurchaseCallback() {
    const username = influencer?.username || campaign.influencer_username;

    if (currentUser == null) {
      Cookie.set('redirect_to', `/${username}/site/campanhas/${campaign.id}?tickets=${selectedTickets}`, { path: '/', expires: 1 });
      window.location.href = '/public/sign_in';
      return;
    }
    const currentTickets = Number(selectedTickets) || 0;
    const suggestedTickets = (Math.floor(currentTickets / 5) + 1) * 5;
    const maxCap = maxTickets();
    if (suggestedTickets > maxCap) {
      submitQuickPurchaseOrder(currentTickets);
      return;
    }
    const suggestedTotalFormatted = numberAsCurrency(suggestedTickets * campaign.ticket_price);
    setRoundUpModal({
      open: true,
      currentTickets,
      suggestedTickets,
      suggestedTotalFormatted,
    });
  }

  function handleRoundUpAccept() {
    const amount = roundUpModal.suggestedTickets;
    setRoundUpModal((s) => ({ ...s, open: false }));
    submitQuickPurchaseOrder(amount);
  }

  // Fechou o modal sem escolher: desistiu da compra, nenhum pedido é criado.
  function handleRoundUpClose() {
    setRoundUpModal((s) => ({ ...s, open: false }));
  }

  function handleRoundUpDecline() {
    const amount = roundUpModal.currentTickets;
    setRoundUpModal((s) => ({ ...s, open: false }));
    submitQuickPurchaseOrder(amount);
  }

  React.useEffect(() => {
    if (!campaign) return;
    setSelectedTickets(initialTicketSelection(campaign));
  }, [campaign?.id]);

  if (!campaign) return null;

  const endDate = moment(campaign.end_time).format('DD/MM');

  if (mode === 'first') {
    return (
      <>
      <RoundUpPurchaseModal
        open={roundUpModal.open}
        onClose={handleRoundUpClose}
        onAccept={handleRoundUpAccept}
        onDecline={handleRoundUpDecline}
        currentTickets={roundUpModal.currentTickets}
        suggestedTickets={roundUpModal.suggestedTickets}
        suggestedTotalFormatted={roundUpModal.suggestedTotalFormatted}
      />
      <div className={classes.rootFirst}>
        <div className={classes.imageContainerFirst} style={{ aspectRatio: campaignBannerAspectRatio(isMobile) }}>
          <PictureBanner
            avifUrl={isMobile ? campaign.mobile_banner_url : campaign.main_banner_url}
            webpUrl={isMobile ? campaign.mobile_banner_webp_url : campaign.main_banner_webp_url}
            fallbackUrl={isMobile ? campaign.mobile_banner_url : campaign.main_banner_url}
            videoUrl={isMobile ? campaign.mobile_banner_video_url : campaign.main_banner_video_url}
          />
          <div className={classes.badgeOverlay}>
            <span className={classes.status} style={badgeStyleForStatus(campaign.user_status)}>
              {statusLabel()}
            </span>
          </div>
        </div>
        <div className={classes.detailsFirst}>
          <div className={classes.titleFirstRow}>
            <Typography className={classes.titleFirst} component="h5">
              {campaign.title}
            </Typography>
          </div>
          {campaign.subtitle && (
            <Typography className={classes.subtitleFirst} variant="body2">
              {campaign.subtitle}
            </Typography>
          )}
          {campaign["open?"] && (campaign.active_subcampaigns || []).filter((s) => !s.sold_out).length > 0 &&
            (campaign.active_subcampaigns || []).filter((s) => !s.sold_out).map((sub) => (
              <SubcampaignHint key={sub.id} sub={sub} className={classes.subcampaignHint} onBoostExpire={onBoostExpire} />
            ))
          }
          <div className={classes.priceCodeRow}>
            {showHeroPrice && (
              <Typography className={classes.priceFirst}>
                APENAS <span className={classes.priceValueBold}>{numberAsCurrency(campaign.ticket_price)}</span>
              </Typography>
            )}
            <div className={classes.codeDateRow}>
              <span className={classes.codeRowLeft}>
                <span className={classes.codeFirst}>{campaign.code}</span>
              </span>
              <span className={classes.dateRowRight}>
                <span
                  className={`${classes.dateBadgeFirst} ${
                    campaign.user_status === 'finished'
                      ? classes.dateBadgeFirstConcluded
                      : isCompletedCampaignStatus(campaign.user_status) ? classes.dateBadgeFirstCompleted : ''
                  }`}
                >
                  <img src="https://d2zdlj1ifv8u1d.cloudfront.net/images/calendar.svg" />
                  {endDate}
                </span>
              </span>
            </div>
          </div>
          {showHeroButton && (
            <Button
              className={classes.comprarBtn}
              classes={{ label: classes.comprarBtnLabel }}
              onClick={(e) => { e.stopPropagation(); handleClick(); }}
              disableElevation
            >
              <span className={classes.comprarBtnIconWrap}>
                <ArrowForwardIcon className={classes.comprarBtnArrow} />
              </span>
              <span className={classes.comprarBtnTitle}>Comprar</span>
              <span className={classes.comprarSubtitle}>Não fique de fora!</span>
            </Button>
          )}
          {showHeroQuickPurchase && campaign["open?"] && (
            <div onClick={(e) => e.stopPropagation()}>
              <SelectionBox
                selectedTickets={selectedTickets}
                setSelectedTickets={safeSetSelectedTickets}
                purchaseCallback={quickPurchaseCallback}
                campaign={campaign}
                loading={purchaseLoading}
                showTitle={false}
              />
            </div>
          )}
          {link && (
            <button type="button" className={classes.verMaisBtn} onClick={handleClick}>
              Ver mais
            </button>
          )}
        </div>
      </div>
      </>
    );
  }

  return (
    <div className={classes.root} onClick={handleClick} style={link ? { cursor: 'pointer' } : {}}>
      <div className={classes.imageContainer}>
        <PictureBanner
          avifUrl={campaign.square_banner_url}
          webpUrl={campaign.square_banner_webp_url}
          fallbackUrl={campaign.square_banner_url}
          videoUrl={campaign.square_banner_video_url}
        />
      </div>
      <div className={classes.details}>
        <div className={classes.topMetaRow}>
          <span className={classes.status} style={badgeStyleForStatus(campaign.user_status)}>
            {statusLabel()}
          </span>
          <span className={classes.code}>{campaign.code}</span>
        </div>
        <Typography className={classes.title} component="h5">
          {campaign.title}
        </Typography>
        <Typography className={classes.subtitle} variant="body2">
          {campaign.subtitle}
        </Typography>
        <div className={classes.priceDateRow}>
          <Typography className={classes.price}>
            POR APENAS <span className={classes.priceValueBold}>{numberAsCurrency(campaign.ticket_price)}</span>
          </Typography>
          <span
            className={`${classes.dateBadge} ${
              campaign.user_status === 'finished'
                ? classes.dateBadgeConcluded
                : isCompletedCampaignStatus(campaign.user_status) ? classes.dateBadgeCompleted : ''
            }`}
          >
            <img src="https://d2zdlj1ifv8u1d.cloudfront.net/images/calendar.svg" />
            {endDate}
          </span>
        </div>
        {campaign["open?"] && (campaign.active_subcampaigns || []).filter((s) => !s.sold_out).length > 0 &&
          (campaign.active_subcampaigns || []).filter((s) => !s.sold_out).map((sub) => (
            <SubcampaignHint key={sub.id} sub={sub} className={classes.subcampaignHintList} onBoostExpire={onBoostExpire} />
          ))
        }
      </div>
    </div>
  );
}
